import React, { useMemo } from 'react'
import { RoomSession } from '../../models/Room'
import { useUserFilter } from '../../apps/professional-app/fa/common/userFilter/hooks'
import { Col, Container, Row, Stack } from 'react-bootstrap'
import { SourceIcon } from '../../apps/professional-app/fa/PastMeetings/SourceIcon'
import { CreatorIcon } from '../../apps/professional-app/fa/PastMeetings/CreatorIcon'
import { Button } from '../../components/Button/Button'
import './LiveMeetings.scss'
import { LiveBadge } from './LiveBadge'
import { AssistantStatusBadge } from './AssistantStatusBadge'
import { MeetingDateDisplay } from './MeetingDateDisplay'
import { AutoJoinControl } from '../../components/AutoJoin/AutoJoinControl'
import { parseMetadata } from '../../common/util'
import { useEffectivePlatform } from '../../common/useEffectivePlatform'
import { MeetingCreatorDisplay } from './MeetingCreatorDisplay'
import { MeetingParticipantsDisplay } from './MeetingParticipantsDisplay'
import { MeetingTitleDisplay } from './MeetingTitleDisplay'
import { useNavigate } from 'react-router-dom'
import { AddAIAssistantButton } from './AddAIAssistantButton'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { StartCallButton } from './StartCallButton'
import { ChangeMeetingOptions } from './ChangeMeetingOptions'
import { PrivateBadge } from './PrivateBadge'
import { MeetingLanguageDisplay } from '../../apps/professional-app/fa/MeetingSummary/MeetingInfo/Language'
import { useMultiLanguageEnabled } from '../../common/useMultiLanguageEnabled'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'

export interface LiveMeetingListItemProps {
  session: RoomSession
  phoneConfig: MeetingProviderAccountIntegrationConfig | null
  onSessionChange?: (session: RoomSession) => void
  onSessionSelectedForCall?: (details: { session: RoomSession; doCall: boolean }) => void
}

export const LiveMeetingListItem: React.FC<LiveMeetingListItemProps> = ({ session, phoneConfig, onSessionChange, onSessionSelectedForCall }) => {
  const {
    source,
    service_id: serviceId,
    creator_service_id: creatorServiceId,
    zocksAssistantStatus = 'notStarted',
    external: { title = null, platform = null, userAgent = null } = {},
  } = useMemo(() => parseMetadata(session.metadata), [session])

  const effectivePlatform = useEffectivePlatform(source, platform, userAgent)
  const { userFilterValues } = useUserFilter('live')
  const navigate = useNavigate()
  const language = session.config?.language
  const { multiLanguageEnabled } = useMultiLanguageEnabled()
  const { hasQuota } = useZocksQuota()
  const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')

  const onMeetingSummaryOpen = (roomId: string, sessionId: string) => {
    navigate({
      pathname: `/workspace/live-meetings/room/${roomId}/session/${sessionId}/`,
    })
  }

  return (
    <Container fluid className='meeting-card'>
      <Row className='m-0 flex-nowrap'>
        <Col xs={12} lg className='px-0 meeting-info'>
          <Row className='d-flex gap-3 m-0'>
            <Col xs='auto' className='px-0'>
              <SourceIcon source={effectivePlatform ? `${source || serviceId}-${effectivePlatform}` : source || serviceId} />
            </Col>
            <Col className='d-flex flex-column justify-content-center overflow-hidden px-0 meeting-details'>
              <Row className='meeting-status-row m-0 align-items-center'>
                <Col xs='auto' className='p-0'>
                  {creatorServiceId && <CreatorIcon creator={creatorServiceId} />}
                </Col>
                <Col xs='auto' className='p-0'>
                  <MeetingDateDisplay startedAt={session.startedAt} finishedAt={session.finishedAt} />
                </Col>
                <Col xs='auto' className='p-0'>
                  <LiveBadge startedAt={session.startedAt} finishedAt={session.finishedAt} />
                </Col>
                {session.isPrivate ? (
                  <Col xs='auto' className='p-0'>
                    <PrivateBadge />
                  </Col>
                ) : null}
                <Col xs='auto' className='p-0'>
                  <AssistantStatusBadge assistantStatus={zocksAssistantStatus} />
                </Col>
              </Row>
              <Row>
                <Col className='meeting-title'>
                  <MeetingTitleDisplay title={title} />{' '}
                </Col>
              </Row>
              <Row>
                <Col xs='auto' className='paragraph-medium medium upcoming-created-by-name'>
                  <Stack direction='horizontal' gap={3}>
                    <MeetingCreatorDisplay creatorId={session.roomCreatedBy?.id} userLabels={userFilterValues} />
                    <MeetingParticipantsDisplay creatorId={session.roomCreatedBy?.id} users={session.users} />
                    {multiLanguageEnabled && hasMultiLanguageSupportQuota ? <MeetingLanguageDisplay language={language?.language} /> : null}
                  </Stack>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col lg={'auto'} className='gap-3 py-3 p-md-0 align-content-center'>
          <Stack direction='horizontal' gap={3}>
            <AutoJoinControl session={session} />
            <AddAIAssistantButton session={session} variant='header' />
            <StartCallButton session={session} phoneConfig={phoneConfig} onRoomSessionSelectedForCall={onSessionSelectedForCall} />
            <Button variant='lightPurple' className='button-small' onClick={() => onMeetingSummaryOpen(session.roomId, session.id)}>
              View
            </Button>
            <ChangeMeetingOptions
              session={session}
              onSessionChange={onSessionChange}
              phoneConfig={phoneConfig}
              onSessionSelectedForCall={onSessionSelectedForCall}
            />
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
